import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MapPin, Plus, PencilSimple, Trash, MagnifyingGlass, UploadSimple, Spinner, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shizaiApi } from '@/pages/shizai/shizaiApi';

const EMPTY = {
  code: '', name: '', formal_name: '', department: '', kana: '', postal_code: '',
  address: '', tel: '', fax: '', contact: '', email: '', notes: '', active: true,
};

const FIELDS = [
  ['code', '納入先コード（数字・8桁ゼロ埋め）', '例: 00001000', true],
  ['name', '納入先（略称）', '例: 技術部', true],
  ['formal_name', '納入先正式名', '例: 株式会社センスビー 技術部', true],
  ['department', '部門', '例: 技術営業第１課', false],
  ['kana', 'カナ', '例: ｷﾞｼﾞｭﾂﾌﾞ', false],
  ['postal_code', '郵便番号', '例: 100-0001', false],
  ['address', '住所', '', false],
  ['tel', '電話番号', '', false],
  ['fax', 'FAX', '', false],
  ['contact', '担当者', '', false],
  ['email', 'メールアドレス', '', false],
  ['notes', '備考', '', false],
];

export default function DeliveryDestinationsSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dlg, setDlg] = useState(null);   // {form, editId, busy}
  const [impOpen, setImpOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impOverwrite, setImpOverwrite] = useState(false);
  const [impBusy, setImpBusy] = useState(false);
  const [impResult, setImpResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await shizaiApi.deliveryDestinations({ include_inactive: true })); }
    catch (e) { toast.error(e?.response?.data?.detail || '納入先の取得に失敗しました'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows
      .filter((r) => showInactive || r.active !== false)
      .filter((r) => !s || [r.code, r.name, r.formal_name, r.kana, r.department, r.address]
        .filter(Boolean).join(' ').toLowerCase().includes(s));
  }, [rows, q, showInactive]);

  const openCreate = () => setDlg({ form: { ...EMPTY }, editId: null, busy: false });
  const openEdit = (r) => setDlg({
    form: Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r[k] ?? (k === 'active' ? true : '')])),
    editId: r.id, busy: false,
  });

  const save = async () => {
    const f = dlg.form;
    if (!f.code.trim() || !f.name.trim() || !f.formal_name.trim()) {
      toast.error('コード・納入先・納入先正式名は必須です'); return;
    }
    setDlg((s) => ({ ...s, busy: true }));
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) =>
      [k, k === 'active' ? !!v : (typeof v === 'string' ? (v.trim() || null) : v)]));
    body.code = f.code.trim(); body.name = f.name.trim(); body.formal_name = f.formal_name.trim();
    try {
      if (dlg.editId) { await shizaiApi.updateDeliveryDestination(dlg.editId, body); toast.success('納入先を更新しました'); }
      else { await shizaiApi.createDeliveryDestination(body); toast.success('納入先を登録しました'); }
      setDlg(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
      setDlg((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const toggleActive = async (r) => {
    try {
      await shizaiApi.updateDeliveryDestination(r.id, {
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r[k] ?? null])),
        code: r.code, name: r.name, formal_name: r.formal_name, active: r.active === false,
      });
      toast.success(r.active === false ? '有効にしました' : '無効にしました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '更新に失敗しました'); }
  };

  const del = async (r) => {
    if (!window.confirm(`納入先「${r.code} ${r.name}」を削除しますか？`)) return;
    try { await shizaiApi.deleteDeliveryDestination(r.id); toast.success('削除しました'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  const runImport = async () => {
    if (!impFile) { toast.error('ファイルを選択してください'); return; }
    setImpBusy(true); setImpResult(null);
    try {
      const res = await shizaiApi.importDeliveryDestinations(impFile, impOverwrite);
      setImpResult(res);
      toast.success(`インポート完了 — 新規 ${res.created}件 / 更新 ${res.updated}件 / スキップ ${res.skipped}件`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'インポートに失敗しました');
    } finally { setImpBusy(false); }
  };

  const shown = filtered.slice(0, 300);

  return (
    <div className="space-y-4" data-testid="dd-settings">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2"
          data-testid="settings-page-title">
          <MapPin size={24} weight="duotone" className="text-teal-700" /> 納入先マスター
        </h1>
        <span className="text-xs text-zinc-400">{rows.length}件（有効 {rows.filter((r) => r.active !== false).length}件）</span>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-teal-600" data-testid="dd-show-inactive" />
            無効も表示
          </label>
          <Button size="sm" variant="outline" onClick={() => { setImpOpen(true); setImpFile(null); setImpResult(null); setImpOverwrite(false); }}
            className="h-8 text-xs text-teal-700 border-teal-300 hover:bg-teal-50" data-testid="dd-import-btn">
            <UploadSimple size={14} className="mr-1" /> インポート
          </Button>
          <Button size="sm" onClick={openCreate} className="h-8 bg-teal-700 hover:bg-teal-800 text-xs" data-testid="dd-add-btn">
            <Plus size={14} className="mr-1" /> 新規登録
          </Button>
        </div>
      </div>

      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="検索 — コード / 納入先 / 正式名 / カナ / 部門 / 住所..."
          className="h-9 pl-9 bg-white" data-testid="dd-search" />
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-zinc-400">読み込み中...</p>
      ) : shown.length === 0 ? (
        <p className="py-12 text-center text-sm text-zinc-400" data-testid="dd-empty">該当する納入先がありません</p>
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="dd-table">
            <thead className="bg-zinc-100 text-zinc-700 text-[10px]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">コード</th>
                <th className="text-left px-2 py-2 font-semibold">納入先</th>
                <th className="text-left px-2 py-2 font-semibold hidden md:table-cell">正式名</th>
                <th className="text-left px-2 py-2 font-semibold hidden lg:table-cell">部門</th>
                <th className="text-left px-2 py-2 font-semibold hidden lg:table-cell">電話番号</th>
                <th className="text-center px-2 py-2 font-semibold">有効</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className={`border-t border-zinc-200 ${r.active === false ? 'opacity-50' : ''}`}
                  data-testid={`dd-row-${r.id}`}>
                  <td className="px-3 py-1.5 font-mono text-zinc-500">{r.code}</td>
                  <td className="px-2 py-1.5 font-medium text-zinc-800">{r.name}</td>
                  <td className="px-2 py-1.5 text-zinc-500 hidden md:table-cell max-w-[260px] truncate">{r.formal_name}</td>
                  <td className="px-2 py-1.5 text-zinc-500 hidden lg:table-cell">{r.department || ''}</td>
                  <td className="px-2 py-1.5 text-zinc-500 hidden lg:table-cell">{r.tel || ''}</td>
                  <td className="px-2 py-1.5 text-center">
                    <Switch checked={r.active !== false} onCheckedChange={() => toggleActive(r)}
                      className="scale-75" data-testid={`dd-active-${r.id}`} />
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <button type="button" onClick={() => openEdit(r)}
                      className="text-zinc-400 hover:text-sky-600 mr-2" data-testid={`dd-edit-${r.id}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button type="button" onClick={() => del(r)}
                      className="text-zinc-400 hover:text-rose-600" data-testid={`dd-delete-${r.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 300 && (
            <p className="px-3 py-2 text-[10px] text-zinc-400 border-t border-zinc-200">
              {filtered.length}件中 300件を表示しています — 検索で絞り込んでください
            </p>
          )}
        </div>
      )}

      <Dialog open={impOpen} onOpenChange={(o) => !o && !impBusy && setImpOpen(false)}>
        <DialogContent className="max-w-md" data-testid="dd-import-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <UploadSimple size={18} weight="duotone" className="text-teal-600" /> 納入先マスターをインポート
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600 space-y-1">
              <p className="font-semibold text-zinc-700">対応形式: .xlsx / .csv（1行目はヘッダー行）</p>
              <p>・旧システム形式: <span className="font-mono">納入先ｺｰﾄﾞ / 納入先正式名 / 納入先略称 ...</span></p>
              <p>・シンプル形式: <span className="font-mono">コード / 納入先 / 納入先正式名 / 部門 / 郵便番号 / 住所 / 電話番号 / 担当者 / 備考 / 有効</span></p>
              <p>・コードは数字1〜8桁（8桁にゼロ埋めされます）</p>
              <button type="button"
                onClick={() => shizaiApi.downloadDeliveryDestTemplate().catch(() => toast.error('テンプレートのダウンロードに失敗しました'))}
                className="inline-flex items-center gap-1 mt-1 text-teal-700 font-semibold hover:underline"
                data-testid="dd-template-download">
                <DownloadSimple size={13} weight="bold" /> テンプレートをダウンロード（.xlsx）
              </button>
            </div>
            <label className="flex items-center gap-2 rounded-md border border-dashed border-zinc-300 px-3 py-2.5 text-xs text-zinc-600 cursor-pointer hover:bg-teal-50 hover:border-teal-300 transition-colors">
              <UploadSimple size={15} />
              <span className="truncate flex-1">{impFile ? impFile.name : 'ファイルを選択（.xlsx / .csv）'}</span>
              <input type="file" accept=".xlsx,.csv" className="hidden" disabled={impBusy}
                onChange={(e) => { setImpFile(e.target.files?.[0] || null); setImpResult(null); e.target.value = ''; }}
                data-testid="dd-import-file" />
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={impOverwrite} onChange={(e) => setImpOverwrite(e.target.checked)}
                className="accent-teal-600" disabled={impBusy} data-testid="dd-import-overwrite" />
              既存コードを上書き更新する（オフの場合はスキップ）
            </label>
            {impResult && (
              <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2 text-xs text-zinc-700 space-y-1"
                data-testid="dd-import-result">
                <p className="font-semibold text-teal-800">
                  新規 {impResult.created}件 ・ 更新 {impResult.updated}件 ・ スキップ {impResult.skipped}件
                </p>
                {impResult.errors?.length > 0 && (
                  <div className="text-rose-700">
                    <p className="font-semibold">エラー {impResult.errors.length}件:</p>
                    {impResult.errors.slice(0, 5).map((e, i) => <p key={i}>・{e}</p>)}
                    {impResult.errors.length > 5 && <p>…ほか{impResult.errors.length - 5}件</p>}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setImpOpen(false)} disabled={impBusy} data-testid="dd-import-close">
              閉じる
            </Button>
            <Button onClick={runImport} disabled={impBusy || !impFile}
              className="bg-teal-700 hover:bg-teal-800" data-testid="dd-import-run">
              {impBusy ? <><Spinner size={14} className="animate-spin mr-1" /> インポート中...</> : 'インポート実行'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dd-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{dlg?.editId ? '納入先を編集' : '納入先を新規登録'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, ph, req]) => (
              <div key={k} className={['formal_name', 'address', 'notes'].includes(k) ? 'sm:col-span-2' : ''}>
                <Label className="text-xs font-semibold text-zinc-600">
                  {label}{req && <span className="text-rose-500 ml-0.5">＊</span>}
                </Label>
                <Input value={dlg?.form?.[k] || ''} placeholder={ph}
                  onChange={(e) => setDlg((s) => ({ ...s, form: { ...s.form, [k]: e.target.value } }))}
                  className="h-9 mt-1 text-sm" data-testid={`dd-field-${k}`} />
              </div>
            ))}
            <label className="sm:col-span-2 inline-flex items-center gap-2 cursor-pointer text-sm text-zinc-700">
              <input type="checkbox" checked={!!dlg?.form?.active}
                onChange={(e) => setDlg((s) => ({ ...s, form: { ...s.form, active: e.target.checked } }))}
                className="accent-teal-600" data-testid="dd-field-active" />
              有効
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDlg(null)} data-testid="dd-dialog-cancel">キャンセル</Button>
            <Button onClick={save} disabled={dlg?.busy} className="bg-teal-700 hover:bg-teal-800" data-testid="dd-dialog-save">
              {dlg?.busy ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
